import React from 'react';
import {
  Route,
  Switch,
  RouteComponentProps,
  BrowserRouter,
  useParams,
  useHistory,
} from 'react-router-dom';

import { makeStyles, createStyles } from '@material-ui/styles';
import {
  SelectedAccountWatchdog,
  usePermissions,
  useTranslation,
} from '@michelin/central-provider';
import { useDialogs } from '@michelin/acid-components';
import ChangeLog from 'components/ChangeLog/ChangeLog';
import Sidebar from 'components/Sidebar';
import { Footer } from 'components/Sidebar/Footer';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';
import { Heading } from 'components/Heading';
import { LocationDetails } from 'components/Location/Detail';
import { ServiceDetails } from 'components/Services/Detail';
import { ContactDetail } from 'components/Contact/Detail';
import { CommercialLocationDetails } from 'components/Commercial/Detail';
import { ContactsList } from 'components/Contact/List';
import { CommercialERSDetail } from 'components/CommercialERS/Detail';
import { CommercialProducts } from 'components/CommercialProducts/CommercialProducts';
import { CommercialNetworks } from 'components/CommercialServices/CommercialNetworks';
import { CommercialTireServices } from 'components/CommercialServices/CommercialTireServices';
import { CommercialMechanicalServices } from 'components/CommercialServices/CommercialMechanicalServices';
import { CommercialOverrides } from 'components/CommercialOverrides/CommercialOverrides';
import { CommercialMassSTUpdate } from 'components/MassSTUpdate/Commercial';
import { TechnicianServiceAreaList } from 'components/TechnicianServiceArea/DetailOfSecondaryLocations/List';
import MassSTUpdate from 'components/MassSTUpdate';
import { LocationList } from 'components/Location/List';
import { SelfServicePromoCodes } from 'components/Admin/SelfServicePromoCodes';
import { TechnicianServiceAreaEdit } from 'components/TechnicianServiceArea/CreateAndEdit/main';
import TechRotationPage from 'components/TechRotation/TechRotation';
import CurrentRouter from './Utils/CurrentRouter';

const drawerWidth = 240;

const HEADER_HEIGHT = runningOnCanvas() ? 0 : 65;

const useStyles = makeStyles(() => createStyles({
  root: {
    width: '100%',
    height: '100%',
    zIndex: 1,
    overflow: 'hidden',
  },
  appFrame: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: '100%',
  },
  navMenu: {
    display: 'flex',
    flex: '1 1 0%',
    height: '100%',
    maxHeight: 'calc(100vh - 5rem)',
  },
  navBar: {
    height: '100%',
    borderRight: '1px solid rgb(231,231,231)',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerContainer: {
    overflow: 'auto',
  },
  drawerPaper: {
    width: drawerWidth,
  },

  privateRouteContainer: {
    display: 'flex',
    height: '100%',
    paddingTop: HEADER_HEIGHT,
  },
  content: {
    width: '100%',
    height: '100%',
    padding: 0,
  },
  mainContent: {
    flexGrow: 1,
    paddingTop: 0,
    overflowX: 'auto',
  },
}));

interface MichelinRouteProps {
  path: string;
  sidebar?: boolean;
  component: any;
  props?: any;
  selectedModule?: string;
  requirePermission?: string;
}

function MichelinRoute(props: MichelinRouteProps) {
  const classes = useStyles();
  const permissions = usePermissions();
  const { btNumber } = useParams<{ btNumber: string }>();
  const history = useHistory();

  if (
    props.requirePermission
    && permissions.location
    && !permissions.allowsAction(props.requirePermission)
  ) {
    history.push(`/${btNumber || ''}`);
    return null;
  }

  return (
    <Route
      exact
      path={props.path}
      render={(routerProps: RouteComponentProps) => {
        CurrentRouter.load(routerProps);
        const cloneRouterProps = props.path === '/' ? {} : routerProps;
        const componentProps = props.props || {};
        return (
          <div className={classes.root}>
            <div className={classes.appFrame}>
              <div className={classes.content}>
                <div className={classes.privateRouteContainer}>
                  <Heading />
                  {permissions.location && props.sidebar ? (
                    <Sidebar selectedModule={props.selectedModule} />
                  ) : null}
                  <main className={classes.mainContent}>
                    {permissions.location && props.component ? (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <props.component
                        {...cloneRouterProps}
                        {...componentProps}
                      />
                    ) : null}
                  </main>
                  {!props.sidebar ? <Footer /> : null}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}

export default function Routes() {
  const { t } = useTranslation();
  const { confirmDialog } = useDialogs();

  CurrentRouter.setTranslationFunction(t);
  CurrentRouter.setConfirmDialog(confirmDialog);

  React.useEffect(() => {
    CurrentRouter.setTranslationFunction(t);
  }, [t]);
  return (
    <SelectedAccountWatchdog>
      <BrowserRouter>
        <Switch>
          <MichelinRoute
            path="/:btNumber/retail/:stNumber/location/view"
            component={LocationDetails}
            props={{ action: 'view' }}
            selectedModule="location"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/retail/:stNumber/location/edit"
            component={LocationDetails}
            props={{ action: 'edit' }}
            selectedModule="location"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/retail/:stNumber/services/view"
            component={ServiceDetails}
            props={{ action: 'view' }}
            selectedModule="service"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/retail/:stNumber/services/edit"
            component={ServiceDetails}
            props={{ action: 'edit' }}
            selectedModule="service"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/contacts/:stNumber/add"
            component={ContactDetail}
            props={{ action: 'add' }}
            selectedModule="contacts"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/contacts/:stNumber/view/:contactId"
            component={ContactDetail}
            props={{ action: 'view' }}
            selectedModule="contacts"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/contacts/:stNumber/edit/:contactId"
            component={ContactDetail}
            props={{ action: 'edit' }}
            selectedModule="contacts"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/contacts/:stNumber/list"
            component={ContactsList}
            props={{ action: 'list' }}
            selectedModule="contacts"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/technician-service-area/:stNumber/list"
            component={TechnicianServiceAreaList}
            props={{ action: 'list' }}
            selectedModule="technician-service-area"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/location/view"
            component={CommercialLocationDetails}
            props={{ action: 'view' }}
            selectedModule="commercial"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/location/edit"
            component={CommercialLocationDetails}
            props={{ action: 'edit' }}
            selectedModule="commercial"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/ers/view"
            component={CommercialERSDetail}
            props={{ action: 'view' }}
            selectedModule="commercial-ers"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/ers/edit"
            component={CommercialERSDetail}
            props={{ action: 'edit' }}
            selectedModule="commercial-ers"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/products/view"
            component={CommercialProducts}
            props={{ action: 'view' }}
            selectedModule="commercial-products"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/products/edit"
            component={CommercialProducts}
            props={{ action: 'edit' }}
            selectedModule="commercial-products"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/networks/view"
            component={CommercialNetworks}
            props={{ action: 'view' }}
            selectedModule="commercial-networks"
            requirePermission="commercial_networks.read"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/networks/edit"
            component={CommercialNetworks}
            props={{ action: 'edit' }}
            selectedModule="networks"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/tire-services/view"
            component={CommercialTireServices}
            props={{ action: 'view' }}
            selectedModule="commercial-tire-services"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/tire-services/edit"
            component={CommercialTireServices}
            props={{ action: 'edit' }}
            selectedModule="commercial-tire-services"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/mechanical-services/view"
            component={CommercialMechanicalServices}
            props={{ action: 'view' }}
            selectedModule="commercial-mechanical-services"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/mechanical-services/edit"
            component={CommercialMechanicalServices}
            props={{ action: 'edit' }}
            selectedModule="commercial-mechanical-services"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/overrides/view"
            component={CommercialOverrides}
            props={{ action: 'view' }}
            selectedModule="commercial-overrides"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/commercial/:stNumber/overrides/edit"
            component={CommercialOverrides}
            props={{ action: 'edit' }}
            selectedModule="commercial-overrides"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/massSTUpdate/commercial/:tab/list"
            props={{ action: 'edit' }}
            component={CommercialMassSTUpdate}
          />
          <MichelinRoute
            path="/:btNumber/massSTUpdate/:tab"
            component={MassSTUpdate}
          />
          <MichelinRoute
            path="/:btNumber/technicianServiceArea/edit"
            props={{ action: 'edit' }}
            component={TechnicianServiceAreaEdit}
          />
          <MichelinRoute
            path="/:btNumber/technicianServiceArea/view"
            props={{ action: 'view' }}
            component={TechnicianServiceAreaEdit}
          />
          <MichelinRoute
            path="/:btNumber/technicianServiceArea/:stNumber/edit"
            props={{ action: 'edit' }}
            component={TechnicianServiceAreaEdit}
          />
          <MichelinRoute
            path="/:btNumber/technicianServiceArea/:stNumber/view"
            props={{ action: 'view' }}
            component={TechnicianServiceAreaEdit}
          />
          <MichelinRoute
            path="/:btNumber/edit"
            component={LocationList}
            props={{ action: 'edit' }}
          />
          <MichelinRoute
            path="/:btNumber/change-history-v2/:stNumber/list"
            component={ChangeLog}
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/change-history/all/list"
            component={ChangeLog}
          />
          <MichelinRoute
            path="/:btNumber/change-history/:stNumber/list"
            component={ChangeLog}
            selectedModule="change-history"
            sidebar
          />
          <MichelinRoute
            path="/:btNumber/admin/self-service-promo-upload"
            component={SelfServicePromoCodes}
          />
          <MichelinRoute
            path="/:btNumber/tech-rotation/:stNumber"
            component={TechRotationPage}
            selectedModule="tech-rotation"
            requirePermission="technician_rotation.read"
            sidebar
          />
          <MichelinRoute path="/:btNumber" component={LocationList} />
          <MichelinRoute path="/" component={React.Fragment} />
          <MichelinRoute path="" component={React.Fragment} />
        </Switch>
      </BrowserRouter>
    </SelectedAccountWatchdog>
  );
}
